import React from 'react';
import { Alert, AlertTitle, LinearProgress, FormControlLabel, Checkbox } from '@mui/material';
import styled from '@emotion/styled';
import { useHasPermission } from '../utils/permissionUtils';

const StyledAlert = styled(Alert)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    '& .MuiAlert-message': {
        width: '100%',
    },
}));

const Meter = styled(LinearProgress)(({ theme }) => ({
    marginTop: theme.spacing(1),
    height: 6,
    borderRadius: 3,
}));

const OverrideControl = styled(FormControlLabel)(({ theme }) => ({
    marginTop: theme.spacing(1),
    marginLeft: 0,
    alignItems: 'flex-start',
}));

const RetainerOverageNotice = ({ status, requireOverride = false, override = false, onOverrideChange }) => {
    const hasPermission = useHasPermission();

    if (!status || !status.hasRetainer || status.level === 'ok') {
        return null;
    }

    const { name, utilizationPct, hoursAllotted, hoursUsed, frequency, periodWord, level } = status;
    const period = periodWord || 'period';
    const usage = `${hoursUsed} h of ${hoursAllotted} h`;
    const cappedValue = Math.min(utilizationPct, 100);

    const isBlock = level === 'block';
    const isInternal = hasPermission('canSeeInternal');
    const severity = isInternal ? (isBlock ? 'error' : 'warning') : 'info';
    const meterColor = isInternal ? severity : 'info';
    let title;
    let body;
    if (isInternal) {
        title = isBlock ? 'Retainer exhausted — no overage' : 'Retainer running low — no overage';
        body = isBlock
            ? `${name} is at ${utilizationPct}% of its ${hoursAllotted} h ${frequency} retainer (${usage}). New projects, tasks, time entries, and charges are blocked until overage is authorized for the item or the next period begins.`
            : `${name} has used ${utilizationPct}% of its ${hoursAllotted} h ${frequency} retainer (${usage}) this ${period}. Overage is not enabled.`;
    } else {
        title = isBlock ? `This ${period}'s retainer hours are used up` : `Retainer hours this ${period}`;
        body = isBlock
            ? `${name} has used ${usage} of its ${frequency.toLowerCase()} retainer. Your hours renew at the start of the next ${period}, and your account manager can help if you need more before then.`
            : `${name} has used ${usage} (${utilizationPct}%) of its ${frequency.toLowerCase()} retainer this ${period}.`;
    }

    return (
        <StyledAlert severity={severity}>
            <AlertTitle>{title}</AlertTitle>
            {body}
            <Meter variant="determinate" value={cappedValue} color={meterColor} />
            {requireOverride && isBlock && (
                <OverrideControl
                    control={
                        <Checkbox
                            checked={override}
                            onChange={(e) => onOverrideChange && onOverrideChange(e.target.checked)}
                        />
                    }
                    label="The client is aware and has authorized overage for this item. (Required to continue — not saved.)"
                />
            )}
        </StyledAlert>
    );
};

export default RetainerOverageNotice;
